import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { walletApi } from '../lib/api';
import { formatCurrency, formatDate, formatTime, getStatusStyle, copyToClipboard } from '../lib/utils';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Check, Clock, X, Copy, Share2, AlertCircle, RefreshCw } from 'lucide-react';

const TransactionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [transaction, setTransaction] = useState(null);

  useEffect(() => { fetchTransaction(); }, [id]);

  const fetchTransaction = async () => {
    try {
      const res = await walletApi.getTransaction(id);
      setTransaction(res.data);
    } catch (error) {
      toast.error('Failed to load transaction');
      navigate('/transactions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async (text, label) => {
    await copyToClipboard(text);
    toast.success(`${label} copied`);
  };

  const handleShare = async () => {
    const text = `Transaction Receipt\n\nAmount: ${formatCurrency(transaction.amount)}\nTo/From: ${transaction.counterparty_name}\nDate: ${formatDate(transaction.created_at)}\nRef: ${transaction.transaction_id}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Transaction Receipt', text }); } catch {}
    } else {
      await copyToClipboard(text);
      toast.success('Receipt copied to clipboard');
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="p-6 lg:p-0 space-y-6">
          <Skeleton className="h-8 w-48" /><Skeleton className="h-48 w-full rounded-xl" /><Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </AppLayout>
    );
  }

  const statusStyle = getStatusStyle(transaction.status);
  const StatusIcon = transaction.status === 'completed' ? Check : transaction.status === 'failed' ? X : Clock;

  return (
    <AppLayout>
      <div className="min-h-screen lg:min-h-0 pb-8 lg:max-w-2xl lg:mx-auto">
        {/* Header */}
        <div className="p-6 lg:p-0 lg:mb-6 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/5 rounded-lg transition-colors lg:hidden"><ArrowLeft className="w-6 h-6" /></button>
          <h1 className="text-xl lg:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Transaction Details</h1>
        </div>
        
        {/* Status Header */}
        <div className="px-6 lg:px-0 pb-6">
          <div className="bg-card border border-white/10 rounded-2xl p-8 text-center">
            <div className={`w-16 h-16 mx-auto rounded-full ${statusStyle.bg} flex items-center justify-center mb-4`}><StatusIcon className={`w-8 h-8 ${statusStyle.text}`} /></div>
            <p className={`text-4xl font-bold balance-text mb-2 ${transaction.direction === 'in' ? 'text-green-500' : ''}`}>
              {transaction.direction === 'in' ? '+' : '-'}{formatCurrency(transaction.amount)}
            </p>
            <span className={`inline-block text-sm px-3 py-1 rounded-full ${statusStyle.bg} ${statusStyle.text}`}>{statusStyle.label}</span>
          </div>
        </div>
        
        {transaction.status === 'failed' && (
          <div className="px-6 lg:px-0 pb-6">
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div><p className="font-medium text-red-500">Transaction Failed</p><p className="text-sm text-muted-foreground mt-1">{transaction.failure_reason || 'This transaction could not be completed. Your funds have been returned.'}</p></div>
            </div>
          </div>
        )}
        
        {/* Details */}
        <div className="px-6 lg:px-0 pb-6">
          <h3 className="text-sm font-medium text-muted-foreground mb-3">Details</h3>
          <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
            <DetailRow label={transaction.direction === 'in' ? 'From' : 'To'} value={transaction.counterparty_name} />
            <DetailRow label="Type" value={transaction.source_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} />
            <DetailRow label="Date" value={formatDate(transaction.created_at)} />
            <DetailRow label="Time" value={formatTime(transaction.created_at)} />
            {transaction.fee > 0 && <DetailRow label="Fee" value={formatCurrency(transaction.fee)} />}
            {transaction.note && <DetailRow label="Note" value={transaction.note} />}
            <DetailRow label="Reference" value={transaction.transaction_id.slice(0, 8) + '...'} copyable fullValue={transaction.transaction_id} onCopy={handleCopy} />
          </div>
        </div>
        
        <div className="px-6 lg:px-0 pb-6">
          <div className="bg-card border border-white/10 rounded-xl p-4 flex items-center justify-between">
            <span className="text-muted-foreground">Balance After</span>
            <span className="font-medium">{formatCurrency(transaction.balance_after)}</span>
          </div>
        </div>
        
        <div className="px-6 lg:px-0 space-y-3">
          <Button onClick={handleShare} className="w-full h-12 bg-primary hover:bg-red-600 text-white font-semibold" data-testid="share-receipt"><Share2 className="w-5 h-5 mr-2" />Share Receipt</Button>
          {transaction.status === 'failed' && <Button onClick={() => navigate('/send')} variant="outline" className="w-full h-12"><RefreshCw className="w-5 h-5 mr-2" />Try Again</Button>}
          <Button variant="ghost" className="w-full h-12 text-muted-foreground" onClick={() => toast.info('Support coming soon')}>Report a Problem</Button>
        </div>
      </div>
    </AppLayout>
  );
};

const DetailRow = ({ label, value, copyable, fullValue, onCopy }) => (
  <div className="flex items-center justify-between p-4 border-b border-white/10 last:border-0">
    <span className="text-muted-foreground">{label}</span>
    <div className="flex items-center gap-2">
      <span className="font-medium">{value}</span>
      {copyable && <button onClick={() => onCopy(fullValue || value, label)} className="p-1 hover:bg-white/5 rounded transition-colors"><Copy className="w-4 h-4 text-muted-foreground" /></button>}
    </div>
  </div>
);

export default TransactionDetailPage;
